import { Pencil, Trash2 } from "lucide-react";
import { Tabs } from "./model";

export const getColumns = (
  tab: Tabs,
  handlers?: {
    openEditModal?: (record: any) => void;
    handleDeleteCogs?: (record: any) => void;
  },
) => {
  switch (tab) {
    case "SKU":
      return skuColumns;
    case "Search Query Brand":
      return brandColumns;
    case "Search Query ASIN":
      return asinColumns;
    case "Ads Time Budget":
      return adsTimeBudgetColumns;
    case "Search Query Performance":
      return searchQueryPerformanceColumns;
    case "Search Query Product":
      return searchQueryProductColumns;
    case "Search Query GEOGRAPHY":
      return searchQueryGeographyColumns;
    case "COGS":
      return SearchQueryCOGSColumns(
        handlers?.openEditModal,
        handlers?.handleDeleteCogs,
      );
    default:
      return skuColumns;
  }
};

export const SearchQueryCOGSColumns = (
  openEditModal?: (record: any) => void,
  handleDeleteCogs?: (record: any) => void,
) => [
  {
    title: "Product",
    dataIndex: "product_id",
    key: "product_id",
    width: 200,
  },
  {
    title: "Currency",
    dataIndex: "currency",
    key: "currency",
    width: 100,
  },
  {
    title: "COGS ($)",
    dataIndex: "cogs",
    key: "cogs",
    width: 120,
    render: (value: number) => (
      <div>
        {typeof value === "number"
          ? value.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })
          : "-"}
      </div>
    ),
  },
  {
    title: "Start Date",
    dataIndex: "date_start",
    key: "date_start",
    width: 132,
    render: (value: string) => {
      if (!value) return "-";
      const date = new Date(value);
      if (isNaN(date.getTime())) return "-";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${day}.${month}.${year}`;
    },
  },
  {
    title: "End Date",
    dataIndex: "date_end",
    key: "date_end",
    width: 132,
    render: (value: string) => {
      if (!value) return "-";
      const date = new Date(value);
      if (isNaN(date.getTime())) return "-";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${day}.${month}.${year}`;
    },
  },
  {
    title: "Miscellaneous ($)",
    dataIndex: "misc",
    key: "misc",
    width: 140,
    render: (value: number) => (
      <div>
        {typeof value === "number"
          ? value.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })
          : "-"}
      </div>
    ),
  },
  {
    title: "Total Amazon fees ($)",
    dataIndex: "fees",
    key: "fees",
    width: 140,
    render: (value: number) => (
      <div>
        {typeof value === "number"
          ? value.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })
          : "-"}
      </div>
    ),
  },
  {
    title: "Actions",
    key: "actions",
    width: 80,
    render: (_: any, record: any) => (
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button
          onClick={() => {
            if (openEditModal) openEditModal(record);
          }}
        >
          <Pencil size={16} color="#B5B5B5" />
        </button>
        <button
          onClick={() => {
            if (handleDeleteCogs) handleDeleteCogs(record);
          }}
        >
          <Trash2 size={16} color="#B5B5B5" />
        </button>
      </div>
    ),
  },
];

export const searchQueryGeographyColumns = [
  {
    title: "Region-Based Search Volume",
    dataIndex: "regionBasedSearchVolume",
    key: "regionBasedSearchVolume",
    width: 200,
  },
  {
    title: "Regional Click-Through Rate (CTR)	",
    dataIndex: "regionalCTR",
    key: "regionalCTR",
    width: 200,
  },
  {
    title: "Competitor Presence by Region	",
    dataIndex: "competitorPresence",
    key: "competitorPresence",
    width: 200,
    render: (text: string) => {
      let color = "#5295E0";
      switch (text) {
        case "High":
          color = "#FE5858";
          break;
        case "Medium":
          color = "#C8A857";
          break;
        default:
          color = "#5295E0";
      }
      return <div style={{ color }}>{text}</div>;
    },
  },
  {
    title: "Localized Keyword Performance",
    dataIndex: "localizedKeywordPerformance",
    key: "localizedKeywordPerformance",
    width: 550,
  },
  {
    title: "Actions",
    key: "actions",
    width: 80,
    render: (_: any, record: any) => (
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button
          onClick={() => {
            console.log(`Edit row with ASIN = ${record.asin}`);
          }}
        >
          <Pencil size={16} color="#B5B5B5" />
        </button>
        <button
          onClick={() => {
            console.log(`Delete row with ASIN = ${record.asin}`);
          }}
        >
          <Trash2 size={16} color="#B5B5B5" />
        </button>
      </div>
    ),
  },
];

export const searchQueryProductColumns = [
  {
    title: "Product",
    dataIndex: "product",
    key: "product",
    width: 200,
  },
  {
    title: "Top Ranking Keywords",
    dataIndex: "topKeywords",
    key: "topKeywords",
    width: 200,
  },
  {
    title: "Keyword Sales Attribution %",
    dataIndex: "keywordSalesAttribution",
    key: "keywordSalesAttribution",
    width: 200,
  },
  {
    title: "Lost Impression Share %",
    dataIndex: "lostImpressionShare",
    key: "lostImpressionShare",
    width: 200,
  },
  {
    title: "Buy Box Win Rate from Search %",
    dataIndex: "buyBoxWinRate",
    key: "buyBoxWinRate",
    width: 200,
  },
  {
    title: "Actions",
    key: "actions",
    width: 80,
    render: (_: any, record: any) => (
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button
          onClick={() => {
            console.log(`Edit row with ASIN = ${record.asin}`);
          }}
        >
          <Pencil size={16} color="#B5B5B5" />
        </button>
        <button
          onClick={() => {
            console.log(`Delete row with ASIN = ${record.asin}`);
          }}
        >
          <Trash2 size={16} color="#B5B5B5" />
        </button>
      </div>
    ),
  },
];

export const searchQueryPerformanceColumns = [
  {
    title: "Brand Visibility Score",
    dataIndex: "brandVisibilityScore",
    key: "brandVisibilityScore",
    width: 200,
  },
  {
    title: "Organic vs. Sponsored Share %",
    dataIndex: "organicVsSponsored",
    key: "organicVsSponsored",
    width: 200,
  },
  {
    title: "Average CPC per Query ($)",
    dataIndex: "averageCpc",
    key: "averageCpc",
    width: 200,
  },
  {
    title: "Competitor Click Share",
    dataIndex: "clickShare",
    key: "clickShare",
    width: 200,
  },
  {
    title: "Historical Trends",
    dataIndex: "historicalTrends",
    key: "historicalTrends",
    width: 200,
    render: (text: string) => {
      let color = "#5295E0";
      switch (text) {
        case "Declining":
          color = "#FE5858";
          break;
        case "Rising":
          color = "#80C67A";
          break;
        default:
          color = "#5295E0";
      }
      return <div style={{ color }}>{text}</div>;
    },
  },
  {
    title: "Actions",
    key: "actions",
    width: 80,
    render: (_: any, record: any) => (
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button
          onClick={() => {
            console.log(`Edit row with ASIN = ${record.asin}`);
          }}
        >
          <Pencil size={16} color="#B5B5B5" />
        </button>
        <button
          onClick={() => {
            console.log(`Delete row with ASIN = ${record.asin}`);
          }}
        >
          <Trash2 size={16} color="#B5B5B5" />
        </button>
      </div>
    ),
  },
];

export const adsTimeBudgetColumns = [
  {
    title: "Campaign Name & Type",
    dataIndex: "campaignName",
    key: "campaignName",
    width: 240,
  },
  {
    title: "Time-Based Budget Consumption",
    dataIndex: "budgetConsumption",
    key: "budgetConsumption",
    width: 163,
  },
  {
    title: "Budget Depletion Rate %",
    dataIndex: "budgetDepletionRate",
    key: "budgetDepletionRate",
    width: 163,
  },
  {
    title: "Missed Impressions Due to Budget Cap",
    dataIndex: "missedImpressions",
    key: "missedImpressions",
    width: 163,
  },
  {
    title: "Best Performing Hours",
    dataIndex: "bestPerformingHours",
    key: "bestPerformingHours",
    width: 163,
  },
  {
    title: "Conversion Rate by Hour",
    dataIndex: "conversionRate",
    key: "conversionRate",
    width: 120,
  },
  {
    title: "Actions",
    key: "actions",
    width: 80,
    render: (_: any, record: any) => (
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button
          onClick={() => {
            console.log(`Edit row with ASIN = ${record.asin}`);
          }}
        >
          <Pencil size={16} color="#B5B5B5" />
        </button>
        <button
          onClick={() => {
            console.log(`Delete row with ASIN = ${record.asin}`);
          }}
        >
          <Trash2 size={16} color="#B5B5B5" />
        </button>
      </div>
    ),
  },
];

export const asinColumns = [
  {
    title: "ASIN",
    dataIndex: "asin",
    key: "asin",
    width: 120,
  },
  {
    title: "Search Queries Associated with ASIN",
    dataIndex: "searchQueries",
    key: "searchQueries",
    width: 300,
  },
  {
    title: "Click Share Per Query %",
    dataIndex: "clickShare",
    key: "clickShare",
    width: 120,
  },
  {
    title: "Purchase Rate Per Query %",
    dataIndex: "purchaseRate",
    key: "purchaseRate",
    width: 120,
  },
  {
    title: "Search Volume Trends",
    dataIndex: "searchVolumeTrends",
    key: "searchVolumeTrends",
    width: 120,
    render: (text: string) => {
      let color = "#5295E0";
      switch (text) {
        case "Declining":
          color = "#FE5858";
          break;
        case "Rising":
          color = "#80C67A";
          break;
        default:
          color = "#5295E0";
      }
      return <div style={{ color }}>{text}</div>;
    },
  },
  {
    title: "Ranking Position",
    dataIndex: "rankingPosition",
    key: "rankingPosition",
    width: 120,
  },
  {
    title: "Actions",
    key: "actions",
    width: 80,
    render: (_: any, record: any) => (
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button
          onClick={() => {
            console.log(`Edit row with ASIN = ${record.asin}`);
          }}
        >
          <Pencil size={16} color="#B5B5B5" />
        </button>
        <button
          onClick={() => {
            console.log(`Delete row with ASIN = ${record.asin}`);
          }}
        >
          <Trash2 size={16} color="#B5B5B5" />
        </button>
      </div>
    ),
  },
];

export const skuColumns = [
  {
    title: "ASIN",
    dataIndex: "asin",
    key: "asin",
    width: 120,
  },
  {
    title: "Date Start",
    dataIndex: "dateStart",
    key: "dateStart",
    width: 132,
  },
  {
    title: "Date End",
    dataIndex: "dateEnd",
    key: "dateEnd",
    width: 132,
  },
  {
    title: "COGS",
    dataIndex: "cogs",
    key: "cogs",
    width: 132,
  },
  {
    title: "Price",
    dataIndex: "price",
    key: "price",
    width: 140,
    render: (text: string, record: any) => (
      <div>
        {text && (
          <>
            <div>{text}</div>
            <div style={{ color: "#888" }}>Net Processed</div>
          </>
        )}
        {record.contributionProfit && (
          <>
            <div>{record.contributionProfit}</div>
            <div style={{ color: "#888" }}>Contribution Profit</div>
          </>
        )}
      </div>
    ),
  },
  {
    title: "Units Sold, Items",
    dataIndex: "unitsSold",
    key: "unitsSold",
    width: 132,
  },
  {
    title: "Units Returned",
    dataIndex: "unitsReturned",
    key: "unitsReturned",
    width: 180,
    render: (text: string, record: any) => (
      <div>
        {text && (
          <>
            <div>{text}</div>
            <div style={{ color: "#888" }}>Net Sales</div>
          </>
        )}
        {record.netProceeds && (
          <>
            <div>{record.netProceeds}</div>
            <div style={{ color: "#888" }}>Net Proceeds</div>
          </>
        )}
        {record.netMargin && (
          <>
            <div>{record.netMargin}</div>
            <div style={{ color: "#888" }}>Net Margin</div>
          </>
        )}
      </div>
    ),
  },
  {
    title: "Amazon Fee",
    dataIndex: "amazonFee",
    key: "amazonFee",
    width: 180,
    render: (text: string, record: any) => (
      <div>
        {text && (
          <>
            <div>{text}</div>
            <div style={{ color: "#888" }}>Base Fulfillment Fee</div>
          </>
        )}
        {record.fbaFee && (
          <>
            <div>{record.fbaFee}</div>
            <div style={{ color: "#888" }}>FBA Fulfillment Fees</div>
          </>
        )}
        {record.refundFee && (
          <>
            <div>{record.refundFee}</div>
            <div style={{ color: "#888" }}>Refund Administration Fee</div>
          </>
        )}
        {record.referalFee && (
          <>
            <div>{record.referalFee}</div>
            <div style={{ color: "#888" }}>Referral Fee</div>
          </>
        )}
        {record.monthlyFee && (
          <>
            <div>{record.monthlyFee}</div>
            <div style={{ color: "#888" }}>Monthly Inventory Storage Fee</div>
          </>
        )}
      </div>
    ),
  },
  {
    title: "Actions",
    key: "actions",
    width: 80,
    render: (_: any, record: any) => (
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button
          onClick={() => {
            console.log(`Edit row with ASIN = ${record.asin}`);
          }}
        >
          <Pencil size={16} color="#B5B5B5" />
        </button>
        <button
          onClick={() => {
            console.log(`Delete row with ASIN = ${record.asin}`);
          }}
        >
          <Trash2 size={16} color="#B5B5B5" />
        </button>
      </div>
    ),
  },
];

export const brandColumns = [
  {
    title: "Search Term",
    dataIndex: "searchTerm",
    key: "searchTerm",
    width: 200,
  },
  {
    title: "Search Volume",
    dataIndex: "searchVolume",
    key: "searchVolume",
    width: 100,
  },
  {
    title: "Click Share %",
    dataIndex: "clickShare",
    key: "clickShare",
    width: 100,
  },
  {
    title: "Conversion Rate %",
    dataIndex: "conversionRate",
    key: "conversionRate",
    width: 120,
  },
  {
    title: "Top ASINs for the Search Query",
    dataIndex: "topAsins",
    key: "topAsins",
    width: 300,
  },
  {
    title: "Share of Voice (SOV)",
    dataIndex: "sov",
    key: "sov",
    width: 120,
  },
  {
    title: "Total Sales from Search Query ($)",
    dataIndex: "totalSales",
    key: "totalSales",
    width: 120,
  },
  {
    title: "Actions",
    key: "actions",
    width: 80,
    render: (_: any, record: any) => (
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button
          onClick={() => {
            console.log(`Edit row with ASIN = ${record.asin}`);
          }}
        >
          <Pencil size={16} color="#B5B5B5" />
        </button>
        <button
          onClick={() => {
            console.log(`Delete row with ASIN = ${record.asin}`);
          }}
        >
          <Trash2 size={16} color="#B5B5B5" />
        </button>
      </div>
    ),
  },
];
